import { SubmissionsService } from './../../../../../services/submissions.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject, OnDestroy, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { NgxSpinnerService } from 'ngx-spinner';
import { LanguageService } from '../../../services/language.service';
import { TranslateComponent } from 'src/app/sharedModule/translate/translate.component';
import { ActivatedRoute, Router  } from '@angular/router';
import { SessionService, SharingContext, ShareDetails } from '../../../../../services/session.service';
import { HttpClient } from '@angular/common/http';
import { ReportService } from '../../../services/report.service';

interface ShareAccess {
  id: string;
  email: string;
  accessType: 'unlimited' | 'one_time' | 'time_based';
  accessPermission: 'view' | 'edit';
  accessTypeDisplay: string;
  shareType: 'email_share' | 'link_share';
  status: string;
  accessCount: number;
  isRevoked: boolean;
  isUsed: boolean;
  isExpired: boolean;
  emailDelivered?: boolean;
  createdAt: string;
  expiresAt?: string;
}

interface OtherShareInfo {
  shareType: 'email_share' | 'link_share';
  accessType: 'unlimited' | 'one_time' | 'time_based';
  accessPermission: 'view' | 'edit';
  sharedBy: string;
  shareCreatedAt: string;
  recipientInfo: string;
}

/**
 * @title Dialog with header, scrollable content and actions
 */
@Component({
  selector: 'privacy-settings-modal',
  templateUrl: './privacy-settings-modal.component.html',
  styleUrls: ['./privacy-settings-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivacySettingsModalComponent implements OnDestroy {
  userInfo: any;
  permissions: any;
  isDocumentInArchive: any;
  sourcesAreDeleted: any;
  submissionUserId: any;
  modalOpen;
  loggedInUser: any;
  isOnIA: boolean;
  isNotStudentPrecheck: any;
  // Sharing context properties
  sharingContext: SharingContext | null = null;
  isSharedSession: boolean = false;
  isViewOnlySession: boolean = false;
  userRole: 'owner' | 'recipient' | null = null;

  // Share Report properties
  shareMethod: 'email' | 'link' | null = null;
  accessType: 'unlimited' | 'one_time' | 'time_based' = 'one_time';
  accessPermission: 'view' | 'edit' = 'view';
  recipientEmail: string = '';
  shareMessage: string = '';
  timeBasedDuration: number = 3600; // 1 hour default
  timeBasedEndDate: string = '';
  isLoadingShare: boolean = false;
  shareAccesses: ShareAccess[] = [];

  // Share details for the current recipient
  currentShareDetails: ShareDetails | null = null;

  // Other shares for the same document
  othersWithAccess: OtherShareInfo[] = [];

  // Current time display and duration calculation
  currentTime: string = '';
  private timeInterval: any;

  // Expandable row management
  expandedRows: Set<string> = new Set();
  expandedSharers: Set<string> = new Set();

  // Others table toggle management
  isOthersTableExpanded: boolean = false;

  // Generated link management
  generatedShareLink: string = '';
  linkCopyMessage: string = '';
  isPrecheckShared: boolean = false;
  hasEditQueryParam: boolean = false;
  isStudentsDocument: boolean = false;

  isPrivacyDataLoaded: boolean = false;

  isEducatorPrecheck = false;

  checkEducatorPrecheck = false;
  checkStudentPrecheck = false;

  deleteDocumentInArchive = false;
  deleteDocumentInRepository = false;
  isConfirmed = false;
  deleteEverythingClicked = false;



  constructor(
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data,
    private acRouter: ActivatedRoute,
    public translate: TranslateService,
    private submissionsService: SubmissionsService,
    private spinner: NgxSpinnerService,
    private languageService: LanguageService,
    private sessionService: SessionService,
    private http: HttpClient,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private ngZone: NgZone,
    private reportService: ReportService
  ) {
    this.userInfo = data.userInfo;
    this.loggedInUser = JSON.parse(localStorage.getItem('user'))
    this.permissions = data.studentDeletionPermissions;
    this.isDocumentInArchive = data.isDocumentInArchive;
    this.sourcesAreDeleted = data.sourcesAreDeleted;
    this.submissionUserId = data.submission.userId;
    this.isNotStudentPrecheck = data.isNotStudentPrecheck;
    // Initialize sharing context data
    this.initializeSharingContext();
  }

  ngOnInit(): void {
    this.isDocumentInArchive = this.isDocumentInArchive === true || this.isDocumentInArchive === 'true';
    this.modalOpen = true;
    this.languageService.selectedLanguage$.subscribe((language: string) => {
      this.translate.use(language);
    });

    if (this.acRouter.snapshot.params.clientName) {
      this.isOnIA = true;
    } else {
      this.isOnIA = false;
    }
    // Start real-time clock
    this.startClock();

    // Load data after component initialization
    setTimeout(() => {
      // Load existing shares for owners
      this.loadExistingShares();

      // Load received shares if user is a recipient
      if (this.userRole === 'recipient') {
        this.loadReceivedShares();
      }
    }, 0);

    this.isPrecheckShared = this.sharingContext?.isPrecheckShared || false;

    // Check for edit query parameter
    this.hasEditQueryParam = this.acRouter.snapshot.queryParams.edit === 'true';

    this.isStudentsDocument = (this.data?.submission?.user?.roleId == 5 || this.data?.submission?.user?.roleId == 9) && !this.hasEditQueryParam;

    // Set privacy data loading state based on session type
    if (this.isSharedSession) {
      this.isPrivacyDataLoaded = false; // Wait for async data in shared sessions
    } else {
      this.isPrivacyDataLoaded = true;  // Show immediately for normal sessions
    }
    
    
      const deleted = localStorage.getItem(`deletedDocument-${this.data.submission.id}`);
      this.deleteDocumentInArchive = deleted === 'true';
  }


  ngOnDestroy(): void {
    // Clean up the interval
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  }

  /**
   * Start the real-time clock that updates every second
   */
  private startClock(): void {
    this.updateCurrentTime();

    // Run the interval outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      this.timeInterval = setInterval(() => {
        // Update the time
        this.updateCurrentTime();

        // Trigger change detection manually inside Angular zone
        this.ngZone.run(() => {
          this.changeDetectorRef.detectChanges();
        });
      }, 1000); // Update every 1000ms (1 second)
    });
  }

  /**
   * Update current time display
   */
  private updateCurrentTime(): void {
    const now = new Date();

    // Format to match datetime-local input format (YYYY-MM-DDTHH:MM:SS)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    this.currentTime = `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Initialize sharing context data from session service
   */
  private initializeSharingContext(): void {
    this.sharingContext = this.sessionService.getSharingContext();
    this.isSharedSession = this.sessionService.isSharedSession();
    this.isViewOnlySession = this.sessionService.isViewOnlySession();
    this.userRole = this.sessionService.getUserRole();
  }

  /**
   * Load existing shares for this report
   */
  private loadExistingShares(): void {
    // Only load shares if user is the owner (can manage shares)
    if (!this.canPerformActions() || this.userRole === 'recipient') {
      this.shareAccesses = [];
      setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      return;
    }

    // Load shares from backend using the correct endpoint
    const submissionId = this.data.submission.id;

    this.http.get<any>(`${environment.apiUrl}/submissions/${submissionId}/shares`).subscribe({
      next: (response) => {
        // Handle the nested response structure
        const sharesArray = response.shares || [];
        this.shareAccesses = sharesArray.map(share => ({
          id: share.id,
          email: share.recipientEmail || this.translate.instant('share_report.link_share'),
          accessType: share.accessType,
          accessPermission: share.accessPermission || 'view',
          accessTypeDisplay: this.getAccessTypeDisplay(share.accessType),
          shareType: share.shareType,
          status: share.status,
          accessCount: share.accessCount,
          isRevoked: share.isRevoked,
          isUsed: share.isUsed,
          isExpired: share.isExpired,
          emailDelivered: share.emailDelivered,
          createdAt: share.shareCreatedAt,
          expiresAt: share.expiresAt
        }));

        // Manually trigger change detection
        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      },
      error: (error) => {
        console.error('❌ Failed to load existing shares:', error);
        // Set empty array on error, don't show error to user for this
        this.shareAccesses = [];
        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      }
    });
  }

  /**
   * Load share details for recipients using the session ID
   * This shows the current share details and others with access
   */
  private loadReceivedShares(): void {
    // Get the current session ID from the sharing context
    const sessionId = this.sessionService.getSessionId();

    if (!sessionId) {
      console.warn('No session ID found for loading share details');
      this.currentShareDetails = null;
      this.othersWithAccess = [];

      this.isPrivacyDataLoaded = true;

      setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      return;
    }

    // Use the session service to get share details
    this.sessionService.getShareDetails(sessionId).subscribe({
      next: (response: ShareDetails) => {
        // Store the full share details
        this.currentShareDetails = response;

        // Store others with access
        this.othersWithAccess = response.othersWithAccess.shares || [];

        this.isPrivacyDataLoaded = true;

        // Manually trigger change detection
        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      },
      error: (error) => {
        console.error('❌ Failed to load share details:', error);

        if (error.status === 404) {
          console.error('Share not found');
        } else if (error.status === 403) {
          console.error('Access denied to share details');
        }

        this.currentShareDetails = null;
        this.othersWithAccess = [];

        this.isPrivacyDataLoaded = true;

        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      }
    });
  }



  onShareMethodKeydown(event: KeyboardEvent, method: 'email' | 'link'): void {
    const emailRadio = document.getElementById('share-email') as HTMLInputElement | null;
    const linkRadio = document.getElementById('share-link') as HTMLInputElement | null;

    if (event.key === 'Tab' && !event.shiftKey && method === 'email' && linkRadio) {
      event.preventDefault();
      linkRadio.focus();
      return;
    }

    if (event.key === 'Tab' && event.shiftKey && method === 'link' && emailRadio) {
      event.preventDefault();
      emailRadio.focus();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.setShareMethod(method);
      return;
    }

    if ((event.key === 'ArrowRight' || event.key === 'ArrowDown') && method === 'email' && linkRadio) {
      event.preventDefault();
      linkRadio.focus();
      if (this.shareMethod !== 'link') {
        this.selectShareMethod('link');
      }
      return;
    }

    if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && method === 'link' && emailRadio) {
      event.preventDefault();
      emailRadio.focus();
      if (this.shareMethod !== 'email') {
        this.selectShareMethod('email');
      }
    }
  }

  private selectShareMethod(method: 'email' | 'link'): void {
    this.shareMethod = method;
    this.generatedShareLink = '';
    this.linkCopyMessage = '';
    this.recipientEmail = '';
    this.shareMessage = '';
    this.accessType = 'one_time';
    this.timeBasedEndDate = '';
    this.timeBasedDuration = 3600;
  }

  /**
   * Set share method (email or link) - allows deselecting
   */
  setShareMethod(method: 'email' | 'link'): void {
    // If clicking the same method, deselect it
    if (this.shareMethod === method) {
      this.shareMethod = null;
    } else {
      this.selectShareMethod(method);
      return;
    }

    // Clear generated link when switching methods
    this.generatedShareLink = '';
    this.linkCopyMessage = '';

    // Reset form fields when switching
    this.recipientEmail = '';
    this.shareMessage = '';
    this.accessType = 'one_time';
    this.timeBasedEndDate = '';
    this.timeBasedDuration = 3600;
  }

  /**
   * Clear share method selection
   */
  clearShareMethod(): void {
    this.shareMethod = null;

    // Clear generated link
    this.generatedShareLink = '';
    this.linkCopyMessage = '';

    // Reset all form fields
    this.recipientEmail = '';
    this.shareMessage = '';
    this.accessType = 'one_time';
    this.timeBasedEndDate = '';
    this.timeBasedDuration = 3600;
  }

  /**
   * Check if email is valid for enabling next steps
   */
  get isEmailValid(): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return this.recipientEmail.trim() !== '' && emailRegex.test(this.recipientEmail.trim());
  }

  /**
   * Check if access type dropdown should be enabled
   */
  get isAccessTypeEnabled(): boolean {
    return this.shareMethod === 'email' ? this.isEmailValid : true;
  }

  /**
   * Check if time-based options should be shown
   */
  get shouldShowTimeOptions(): boolean {
    return this.accessType === 'time_based' && this.isAccessTypeEnabled;
  }

  /**
   * Check if invite button should be enabled
   */
  get isInviteButtonEnabled(): boolean {
    if (this.isLoadingShare || !this.isEmailValid) {
      return false;
    }

    // If time-based access is selected, require valid future end date
    if (this.accessType === 'time_based') {
      if (!this.timeBasedEndDate) {
        return false;
      }

      const now = new Date();
      const endDate = new Date(this.timeBasedEndDate);

      // Check if the end date is valid and in the future
      return endDate.getTime() > now.getTime();
    }

    return true;
  }

  /**
   * Check if copy link button should be enabled
   */
  get isCopyLinkButtonEnabled(): boolean {
    if (this.isLoadingShare) {
      return false;
    }

    // If time-based access is selected, require valid future end date
    if (this.accessType === 'time_based') {
      if (!this.timeBasedEndDate) {
        return false;
      }

      const now = new Date();
      const endDate = new Date(this.timeBasedEndDate);

      // Check if the end date is valid and in the future
      return endDate.getTime() > now.getTime();
    }

    return true;
  }

  /**
   * Handle email input changes - reset access type if email is cleared
   */
  onEmailChange(): void {
    if (!this.isEmailValid && this.accessType === 'time_based') {
      // Reset to default access type if email becomes invalid and time was selected
      this.accessType = 'one_time';
      this.timeBasedEndDate = '';
    }
  }

  /**
   * Set access type and handle time-based initialization
   */
  setAccessType(type: 'unlimited' | 'one_time' | 'time_based'): void {
    this.accessType = type;

    // Clear any previously generated link since settings have changed
    this.clearGeneratedLink();

    // Set default 24-hour expiration when time-based is selected
    if (type === 'time_based' && !this.timeBasedEndDate) {
      this.setDefault24HourExpiration();
    }
  }

  /**
   * Set default expiration time to 24 hours from now
   */
  private setDefault24HourExpiration(): void {
    const now = new Date();
    const tomorrow = new Date(now.getTime() + (24 * 60 * 60 * 1000)); // Add 24 hours

    // Format for datetime-local input (YYYY-MM-DDTHH:MM)
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    const hours = String(tomorrow.getHours()).padStart(2, '0');
    const minutes = String(tomorrow.getMinutes()).padStart(2, '0');

    this.timeBasedEndDate = `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  /**
   * Calculate duration in seconds when end date changes
   */
  onEndDateChange(): void {
    this.calculateDuration();
  }

  /**
   * Handle access permission changes and clear generated link
   */
  onAccessPermissionChange(): void {
    // Clear any previously generated link since settings have changed
    this.clearGeneratedLink();
  }

  /**
   * Calculate duration based on end date
   */
  private calculateDuration(): void {
    if (!this.timeBasedEndDate) return;

    const now = new Date();
    const endDate = new Date(this.timeBasedEndDate);
    const diffMs = endDate.getTime() - now.getTime();
    this.timeBasedDuration = Math.max(60, Math.floor(diffMs / 1000)); // Minimum 1 minute
  }

  /**
   * Get access type display text
   */
  getAccessTypeDisplay(type: string): string {
    switch (type) {
      case 'unlimited': return this.translate.instant('share_report.unlimited_access');
      case 'one_time': return this.translate.instant('share_report.one_time_view');
      case 'time_based': return this.translate.instant('share_report.time_based_access');
      default: return type;
    }
  }

  /**
   * Get status display text
   */
  getStatusDisplay(status: string): string {
    switch (status) {
      case 'not_opened': return this.translate.instant('share_report.not_opened');
      case 'used': return this.translate.instant('share_report.status_used');
      case 'active': return this.translate.instant('share_report.active');
      case 'expired': return this.translate.instant('share_report.status_expired');
      case 'revoked': return this.translate.instant('share_report.revoked');
      default: return status;
    }
  }

  /**
   * Get simple status class for clean styling
   */
  getStatusClass(share: ShareAccess): string {
    if (share.isRevoked) return 'status-revoked';
    if (share.isExpired) return 'status-expired';
    if (share.isUsed && share.accessType === 'one_time') return 'status-used';
    if (share.accessCount > 0) return 'status-active';
    return 'status-not-opened';
  }

  /**
   * Get status badge color class
   */
  getStatusBadgeClass(share: ShareAccess): string {
    if (share.isRevoked) return 'status-revoked';
    if (share.isExpired) return 'status-expired';
    if (share.isUsed && share.accessType === 'one_time') return 'status-used';
    if (share.accessCount > 0) return 'status-active';
    if (share.emailDelivered === false) return 'status-delivery-failed';
    return 'status-not-opened';
  }

  /**
   * Get comprehensive access information
   */
  getAccessInfo(share: ShareAccess): string {
    let info = this.getAccessTypeDisplay(share.accessType);

    if (share.accessCount > 0) {
      info += ` (${share.accessCount} access${share.accessCount > 1 ? 'es' : ''})`;
    }

    return info;
  }

  /**
   * Get share type display
   */
  getShareTypeDisplay(shareType: string): string {
    switch (shareType) {
      case 'email_share': return this.translate.instant('general.email');
      case 'link_share': return this.translate.instant('general.link');
      default: return shareType;
    }
  }

  /**
   * Send email invitation
   */
  sendEmailInvitation(): void {
    if (!this.recipientEmail || !this.recipientEmail.includes('@')) {
      swal.fire({
        title: this.translate.instant('share_report.invalid_email'),
        text: this.translate.instant('share_report.please_enter_valid_email'),
        icon: 'error'
      });
      return;
    }

    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error'
      });
      return;
    }

    this.isLoadingShare = true;

    const shareRequest = {
      submissionId: this.data.submission.id,
      shareMethod: 'email',
      recipientEmail: this.recipientEmail,
      accessType: this.accessType,
      accessPermission: this.accessPermission,
      ...(this.accessType === 'time_based' && { duration: this.timeBasedDuration }),
      ...(this.shareMessage && { message: this.shareMessage })
    };

    this.http.post<any>(`${environment.apiUrl}/share-report`, shareRequest).subscribe({
      next: (response) => {
        this.isLoadingShare = false;

        // Store the email for success message before clearing
        const invitedEmail = this.recipientEmail;

        // Clear form fields immediately
        this.recipientEmail = '';
        this.accessType = 'one_time'; // Reset to default
        this.timeBasedEndDate = '';
        this.timeBasedDuration = 3600;

        // Reload the shares list to show the new share
        this.loadExistingShares();

        swal.fire({
          title: this.translate.instant('share_report.invitation_sent'),
          text: `${this.translate.instant('share_report.email_invitation_sent_to')} ${invitedEmail}`,
          icon: 'success',
          timer: 3000,
          showConfirmButton: false
        });
      },
      error: (error) => {
        this.isLoadingShare = false;
        console.error('Share error:', error);
        swal.fire({
          title: this.translate.instant('share_report.share_failed'),
          text: this.translate.instant('share_report.failed_send_invitation'),
          icon: 'error'
        });
      }
    });
  }

  /**
   * Generate and copy share link
   */
  generateShareLink(): void {
    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error'
      });
      return;
    }

    this.isLoadingShare = true;

    const shareRequest = {
      submissionId: this.data.submission.id,
      shareMethod: 'link',
      accessType: this.accessType,
      accessPermission: this.accessPermission,
      ...(this.accessType === 'time_based' && { duration: this.timeBasedDuration })
    };

    this.http.post<any>(`${environment.apiUrl}/share-report`, shareRequest).subscribe({
      next: (response) => {
        this.isLoadingShare = false;

        // Store the generated link
        this.generatedShareLink = response.shareUrl;

        // Reset form state
        this.accessType = 'one_time'; // Reset to default
        this.timeBasedEndDate = '';
        this.timeBasedDuration = 3600;

        // Reload the shares list to show the new share
        this.loadExistingShares();

        // Auto-copy link to clipboard and show success message
        this.copyLinkToClipboard(true);

        swal.fire({
          title: this.translate.instant('share_report.link_generated'),
          text: this.translate.instant('share_report.share_link_generated_copied'),
          icon: 'success',
          timer: 3000,
          showConfirmButton: false
        });
      },
      error: (error) => {
        this.isLoadingShare = false;
        console.error('Share error:', error);
        swal.fire({
          title: this.translate.instant('share_report.link_generation_failed'),
          text: this.translate.instant('share_report.failed_generate_share_link'),
          icon: 'error'
        });
      }
    });
  }

  /**
   * Copy generated link to clipboard
   */
  copyLinkToClipboard(isAutomatic: boolean = false): void {
    if (!this.generatedShareLink) {
      return;
    }

    navigator.clipboard.writeText(this.generatedShareLink).then(() => {
      this.linkCopyMessage = 'Copied!';

      if (!isAutomatic) {
        // Show success message only for manual copy
        swal.fire({
          title: this.translate.instant('share_report.link_copied'),
          text: this.translate.instant('share_report.share_link_copied_clipboard'),
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }

      // Reset the copy message after 2 seconds
      setTimeout(() => {
        this.linkCopyMessage = '';
      }, 2000);
    }).catch(() => {
      this.linkCopyMessage = 'Copy failed';

      // Fallback for browsers that don't support clipboard API
      swal.fire({
        title: this.translate.instant('share_report.copy_failed'),
        text: this.translate.instant('share_report.please_manually_copy_link'),
        input: 'text',
        inputValue: this.generatedShareLink,
        inputAttributes: {
          readonly: 'true'
        },
        showCancelButton: false,
        confirmButtonText: this.translate.instant('share_report.close')
      });

      // Reset the error message after 3 seconds
      setTimeout(() => {
        this.linkCopyMessage = '';
      }, 3000);
    });
  }

  /**
   * Remove share access
   */
  removeShareAccess(shareId: string): void {
    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error'
      });
      return;
    }

    swal.fire({
      title: this.translate.instant('share_report.remove_access'),
      text: this.translate.instant('share_report.confirm_revoke_access'),
      icon: 'warning',
      iconColor: '#740000',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: this.translate.instant('share_report.yes_remove'),
      cancelButtonText: this.translate.instant('share_report.cancel'),
      customClass: {
        popup: 'extra-modal-popup',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        // Remove from backend
        this.http.post(`${environment.apiUrl}/shares/${shareId}/revoke`, {}).subscribe({
          next: () => {
            // Reload the shares list to reflect the removal
            this.loadExistingShares();

            swal.fire({
              title: this.translate.instant('share_report.access_removed'),
              text: this.translate.instant('share_report.share_access_revoked'),
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          },
          error: (error) => {
            console.error('Remove share error:', error);
            swal.fire({
              title: this.translate.instant('share_report.removal_failed'),
              text: this.translate.instant('share_report.failed_to_remove_access'),
              icon: 'error'
            });
          }
        });
      }
    });
  }

  /**
   * Get sharing context display text for debugging/display purposes
   */
  getSharingContextText(): string {
    if (!this.sharingContext) {
      return this.translate.instant('share_report.no_sharing_context_available');
    }

    return JSON.stringify(this.sharingContext, null, 2);
  }

  /**
   * Check if current user can perform actions based on sharing context
   */
  canPerformActions(): boolean {
    // Use enhanced security validation
    return this.sessionService.canPerformSensitiveOperation();
  }

  /**
   * Get sharing status message for display
   */
  getSharingStatusMessage(): string {
    if (!this.isSharedSession) {
      return this.translate.instant('sharing.document_not_shared');
    }

    if (this.userRole === 'owner') {
      return this.translate.instant('sharing.you_are_document_owner');
    }

    if (this.userRole === 'recipient') {
      const viewType = this.isViewOnlySession ?
        this.translate.instant('sharing.view_only_access') :
        this.translate.instant('sharing.edit_access');

      let message = `${this.translate.instant('sharing.shared_document')} - ${viewType}`;

      if (this.sharingContext?.sharedBy) {
        message += `\n${this.translate.instant('sharing.shared_by')}: ${this.sharingContext.sharedBy.name} (${this.sharingContext.sharedBy.email})`;
      }

      return message;
    }

    return this.translate.instant('sharing.unknown_sharing_status');
  }

  deleteEverything() {
    this.deleteEverythingClicked = true;
  }

  deleteSettingsDocument() {
    this.deleteDocument(2);  
    this.cancel();
  }

  /**
   * Open the reset confirmation modal with counts of excluded items
   */
  openResetConfirmationModal(): void {
    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error'
      });
      return;
    }

    // Get counts from data passed to modal
    const reportData = this.data.reportData || {};
    const excludedOriginalSources = reportData.excludedOriginalSources || [];
    const excludedTranslatedSources = reportData.excludedTranslatedSources || [];
    const manipulations = reportData.manipulations || {};
    const metadata = reportData.metadata || null;
    const imagesDetectionData = reportData.imagesDetectionData || [];

    const originalSourcesCount = excludedOriginalSources.length || 0;
    const translatedSourcesCount = excludedTranslatedSources.reduce((total: number, langItem: any) => {
      return total + (langItem.sources ? langItem.sources.length : 0);
    }, 0);
    const metadataFlagsCount = metadata?.excluded_flags?.length || 0;
    const characterReplacementsCount = manipulations?.excludedReplacedCharacters?.reduce(
      (sum: number, item: any) => sum + (item.count || 0), 0
    ) || 0;
    const hiddenTextCount = reportData.excludedHiddenTextCount || 0;
    const imagesCount = (manipulations?.excludedImages?.length || 0) + 
                        imagesDetectionData.filter((img: any) => img.label === 'green').length;

    // Build the list of items to be reset
    const resetItems: string[] = [];
    if (originalSourcesCount > 0) {
      resetItems.push(`${originalSourcesCount} original language source${originalSourcesCount !== 1 ? 's' : ''}`);
    }
    if (translatedSourcesCount > 0) {
      resetItems.push(`${translatedSourcesCount} translated language source${translatedSourcesCount !== 1 ? 's' : ''}`);
    }
    if (metadataFlagsCount > 0) {
      resetItems.push(`${metadataFlagsCount} metadata flag${metadataFlagsCount !== 1 ? 's' : ''}`);
    }
    if (characterReplacementsCount > 0) {
      resetItems.push(`${characterReplacementsCount} character replacement${characterReplacementsCount !== 1 ? 's' : ''}`);
    }
    if (hiddenTextCount > 0) {
      resetItems.push(`${hiddenTextCount} hidden text character${hiddenTextCount !== 1 ? 's' : ''}`);
    }
    if (imagesCount > 0) {
      resetItems.push(`${imagesCount} image${imagesCount !== 1 ? 's' : ''} in place of text`);
    }

    const itemsListHtml = resetItems.length > 0
      ? `<ul class="reset-modal-list">
          ${resetItems.map(item => `<li>${item}</li>`).join('')}
         </ul>`
      : `<p class="reset-modal-empty">${this.translate.instant('report_settings.reset_modal_no_items')}</p>`;

    const headerHtml = `
      <div class="reset-modal-header">
        <h2 id="reset-modal-title" class="reset-modal-title">${this.translate.instant('report_settings.reset_originality_report_question')}</h2>
        <button type="button" class="reset-modal-close" aria-label="Close dialog">
          <img src="../../../../../../assets/report_v2/close_big.svg" alt="" role="presentation">
        </button>
      </div>
      <div class="br-line-modal" style="margin-bottom: 18px;"></div>`;

    const contentHtml = `
      <div id="reset-modal-desc" class="reset-modal-body">
        <p class="reset-modal-intro">${this.translate.instant('report_settings.reset_modal_intro')}</p>
        ${itemsListHtml}
        <p class="reset-modal-warning">${this.translate.instant('report_settings.reset_modal_warning')}</p>
      </div>
      <div class="reset-modal-actions-wrap">
        <button type="button" class="reset-modal-cancel-btn swal2-deny" aria-label="${this.translate.instant('general.cancel')}">${this.translate.instant('reports_io.cancel_button')}</button>
        <button type="button" class="reset-modal-confirm-btn swal2-confirm" aria-label="Confirm reset">${this.translate.instant('reports_io.confirm_button')}</button>
      </div>
    `;

    const wrapperHtml = `<div class="reset-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="reset-modal-title" aria-describedby="reset-modal-desc">${headerHtml}${contentHtml}</div>`;

    const previousActiveElement = document.activeElement as HTMLElement | null;
    let keydownHandler: ((e: KeyboardEvent) => void) | null = null;

    swal.fire({
      html: wrapperHtml,
      showConfirmButton: false,
      showDenyButton: false,
      showCloseButton: false,
      customClass: {
        popup: 'extra-modal-popup reset-confirm-modal',
      },
      didOpen: (popup) => {
        const container = (popup && typeof popup.querySelector === 'function') ? popup : document.querySelector('.reset-confirm-modal');
        if (!container) return;

        const closeBtn = container.querySelector<HTMLButtonElement>('.reset-modal-close');
        const cancelBtn = container.querySelector<HTMLButtonElement>('.reset-modal-cancel-btn');
        const confirmBtn = container.querySelector<HTMLButtonElement>('.reset-modal-confirm-btn');

        const focusables = [cancelBtn, confirmBtn, closeBtn].filter((el): el is HTMLButtonElement => !!el);

        keydownHandler = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            swal.close();
            return;
          }
          if (e.key !== 'Tab') return;
          const currentIndex = focusables.indexOf(document.activeElement as HTMLButtonElement);
          if (currentIndex === -1) return;
          e.preventDefault();
          const nextIndex = e.shiftKey
            ? (currentIndex - 1 + focusables.length) % focusables.length
            : (currentIndex + 1) % focusables.length;
          focusables[nextIndex].focus();
        };

        container.addEventListener('keydown', keydownHandler);

        if (closeBtn) {
          closeBtn.addEventListener('click', () => swal.close());
        }
        if (cancelBtn) {
          cancelBtn.addEventListener('click', () => swal.close());
        }
        if (confirmBtn) {
          confirmBtn.addEventListener('click', () => {
            swal.close();
            this.resetReportToOriginal();
          });
        }

        if (focusables.length > 0) {
          focusables[0].focus();
        }
      },
      didClose: () => {
        const container = document.querySelector('.reset-confirm-modal');
        if (container && keydownHandler) {
          container.removeEventListener('keydown', keydownHandler);
          keydownHandler = null;
        }
        if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
          previousActiveElement.focus();
        }
      },
    });
  }

  /**
   * Reset the report to original state
   */
  resetReportToOriginal(): void {
    this.spinner.show();
    
    this.reportService.resetReportToOriginal(this.data.submission.id).subscribe({
      next: () => {
        this.spinner.hide();
        swal.fire({
          title: 'Report Reset',
          text: 'The originality report has been reset to its original state.',
          icon: 'success',
          timer: 3000,
          showConfirmButton: false
        }).then(() => {
          // Close the settings modal
          this.cancel();
          // Reload the page to show the reset report
          window.location.reload();
        });
      },
      error: (error) => {
        console.log('error', error);
        this.spinner.hide();
        console.error('Reset report error:', error);
        swal.fire({
          title: 'Reset Failed',
          text: error.error?.message || 'Failed to reset report. Please try again.',
          icon: 'error'
        });
      }
    });
  }

  cancel() {
    this.dialog.closeAll();
  }

  submit() {
    // Validate security before submitting
    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error',
        width: '600px',
      });
      return;
    }

    if (this.permissions.deleteEverything) {
      this.permissions.deleteInArchive = true
      // this.permissions.deleteSources = true
    }
    let deleteInArchiveText;
    let deleteEverythingText;
    let deleteSourcesText;
    let title = '';
    if (this.permissions.deleteInArchive && !(this.data.isDocumentInArchive == undefined)) {
      this.isConfirmed = true;
      // title = `<div class="dialog-section-title">${this.translate.instant('app.archive_detele_educator_are_you_sure_you_want_to_give_student_permission_to')}</div>`
      title = `<div style="display: flex; justify-content: space-between; align-items: center;margin-top: -20px;"><div class="title_general_admin">${this.translate.instant('app.archive_detele_educator_are_you_sure_you_want_to_give_student_permission_to')}</div><div class="btn-modal-admin">
      <button class="swal-close-btn" aria-label="Close" style="background:none;border:none;cursor:pointer;margin-left: 62px;margin-bottom: 17px;"><img src="../../../../../../assets/report_v2/close_big.svg" alt="Close button"></button></div></div>`
      deleteInArchiveText = `<div class="dialog-section">${this.translate.instant('report.delete_document_from_archive')}</div><div class="br-line-modal"></div>`
    } else {
      deleteInArchiveText = ''
    }
    if (this.permissions.deleteEverything) {
      title = `<div style="display: flex; justify-content: space-between; align-items: center;margin-top: -20px;"><div class="title_general_admin">${this.translate.instant('app.archive_detele_educator_are_you_sure_you_want_to_give_student_permission_tos')}</div><div class="btn-modal-admin">
      <button class="swal-close-btn" aria-label="Close" style="background:none;border:none;cursor:pointer;margin-left: 32px;margin-bottom: 17px;"><img src="../../../../../../assets/report_v2/close_big.svg" alt="Close button"></button></div></div>`
      deleteEverythingText = `</br><span style="color:red; display: block; font-weight: bold;font-size: 0.8rem;margin-left:20px"></span>`
    } else {
      deleteEverythingText = ''
    }
    if (this.permissions.deleteSources) {
      deleteSourcesText = ``
    } else {
      deleteSourcesText = ''
    }
    if (!deleteInArchiveText && !deleteSourcesText && !deleteEverythingText) {
      title = this.translate.instant('report.student_wont_be_able_to_delete_anything')
    }
    swal.fire({
      title: title,
      html:
        deleteInArchiveText +
        deleteSourcesText +
        deleteEverythingText,
      icon: 'warning',
      iconColor: '#740000',
      showDenyButton: true,
      customClass: {
        popup: 'extra-modal-popup',
      },
      confirmButtonColor: '#004080',
      denyButtonColor: '#F7F7F7',
      confirmButtonText: this.translate.instant('reports_io.confirm_button'),
      denyButtonText: this.translate.instant('reports_io.cancel_button'),
      didOpen: () => {
        const closeBtns = document.querySelectorAll('.swal-close-btn');
        closeBtns.forEach(btn => {
          btn.addEventListener('click', () => {
            swal.close();
          });
        });
      }
    })
      .then((result) => {
        if (result.isConfirmed) {
          this.spinner.show();
          this.submissionsService.deletionPermissions(this.data.submission.id, this.permissions)
            .pipe()
            .subscribe(
              (data: any) => {
                this.spinner.hide();
                // if(!this.isConfirmed) {
                //   swal.fire('', this.translate.instant('delete.permissions_changed_successfully'), 'info');
                // }
                // this.isConfirmed = false;
              },
              (error) => {
                console.log('error', error);
                this.spinner.hide();
              }
            );
        }
      });
  }

  deleteDocument(type) {      
    let title;
    let paragraph;

    let deleteText;
    if (type == 0) {
      title = this.translate.instant('delete.all_the_sources_we_found_similarity_will_be_removed')

      deleteText = `<span style="color:red; display: block; font-weight: bold;font-size: 1rem;margin-left:20px">${this.translate.instant('delete.this_is_permanent')}</span>`,
        paragraph = this.translate.instant('delete.sources_deleted_successfully')
      if (this.data.sourcesAreDeleted) {
        swal.fire(this.translate.instant('app.sources_of_this_document_are_already_deleted'), '', 'info');
        return true;
      }
    } else if (type == 1) {
            if(this.loggedInUser?.id && this.data.submission.archive && this.loggedInUser.roleId === 9){
              title = this.translate.instant('reports_io.remove_modal');
              deleteText = `<div class="dialog-section"><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_case_repository')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph2_repository')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph3')}</div><div class="br-line-modal"></div>`
              paragraph = this.translate.instant('delete.document_has_been_deleted')
            }
          else if (this.isNotStudentPrecheck && this.loggedInUser.roleId === 9){
              // title = "Delete your document permanently?";
              title = this.translate.instant('reports_io.delete_from_archives');
              deleteText = `<div class="dialog-section"><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_case_repository')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph2_repository')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph3')}</div><div class="br-line-modal"></div>`
              paragraph = this.translate.instant('delete.document_has_been_deleted');
          }
      
      else {
      // title = this.translate.instant('delete.your_document_will_be_removed_from_our_archive');
      title = this.translate.instant('reports_io.delete_from_archives');
      deleteText = `<div class="dialog-section"><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph2')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph3')}</div><div class="br-line-modal"></div>`
      paragraph = this.translate.instant('delete.document_deleted_successfully_in_our_archive');
      if (this.data.isDocumentInArchive == false) {
        let info: string = this.translate.instant('reports_io.delete_from_our_archive');
        swal.fire(info, '', 'info');
        return true;
      }}

    } else if (type == 2) {
      if(this.submissionUserId == this.loggedInUser?.id && this.data.submission.archive  && this.permissions.deleteInArchive && this.loggedInUser.roleId === 10){
        title = this.translate.instant('reports_io.delete_from_repository');
        deleteText = `<div class="dialog-section"><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_case_repository')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph2_repository')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph3')}</div><div class="br-line-modal"></div>`
        paragraph = this.translate.instant('delete.document_has_been_deleted')
      } else if ((this.loggedInUser?.id && this.data.submission.archive  && this.permissions.deleteInArchive) && (this.loggedInUser.roleId === 10)){
        title = this.translate.instant('reports_io.delete_from_our_archive_permanently');
        deleteText = `<div class="dialog-section"><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_case_repository')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph2_repository')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it_paragraph3')}</div><div class="br-line-modal"></div>`
        paragraph = this.translate.instant('delete.document_has_been_deleted')
      } else {
        title = this.translate.instant('reports_io.delete_from_our_archive_permanently');
        // title = "Diella";
        deleteText = `<div class="dialog-section"><div style="margin-bottom:8px;">${this.translate.instant('reports_io.delete_permanently_from_io')}</div><br><div style="margin-bottom:8px;">${this.translate.instant('reports_io.action_irreversible')}</div><br><div style="margin-bottom:8px;"></div><div class="br-line-modal"></div>`
        paragraph = this.translate.instant('delete.document_has_been_deleted')
    }
}
  //  let isEducatorPrecheck = (this.submissionUserId == this.loggedInUser?.id && this.data.submission.archive  && this.permissions.deleteInArchive)


  this.checkEducatorPrecheck = this.loggedInUser?.id && this.data.submission.archive && this.loggedInUser.roleId === 10 && this.submissionUserId === this.loggedInUser?.id
  this.checkStudentPrecheck = !this.isNotStudentPrecheck && this.loggedInUser.roleId === 9


    let confirmColor = '#760A0A'; 

    // if (this.loggedInUser.roleId === 9) {      
    //   confirmColor = '#760A0A';    
    // } else if(isEducatorPrecheck){
    //   confirmColor = '#760A0A';    
    // }
    //  else if (this.loggedInUser.roleId === 10) {   
    //   confirmColor = '#004080';   
    // }
    let confirmText = '';

    if (type === 1) {
      confirmText = this.translate.instant('reports_io.yes_confirm');
    } else if (type === 2) {
      confirmText = this.translate.instant('reports_io.yes_permanently');
    }

    const headerHtml = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
      <div class="title_general_modal">
          ${title}
      </div>
      <div class="btn-modal-style">
        <button class="swal-close-btn" aria-label="Close" style="background:none;border:none;cursor:pointer;">
          <img src="../../../../../../assets/report_v2/close_big.svg" alt="Close button">
        </button>
      </div>
    </div>`;


    swal.fire({
      html: `${headerHtml} ${deleteText}`,
      showDenyButton: true,
      confirmButtonText: confirmText,
      confirmButtonColor: confirmColor,
      denyButtonText: this.translate.instant('reports_io.cancel_button'),
      denyButtonColor: '#c62828',
      customClass: {
        popup: 'extra-modal-popup',
      },
      icon: 'warning',
      iconColor: '#740000',
      didOpen: () => {
        const closeBtns = document.querySelectorAll('.swal-close-btn');
        closeBtns.forEach(btn => {
          btn.addEventListener('click', () => swal.close());
        });
      }
    })
      .then((result) => {
        if (result.isConfirmed) {
          if(type == 1) { this.deleteDocumentInArchive = true; }
          if(type == 2) { this.deleteDocumentInRepository = true; }  
          this.spinner.show();
          this.submissionsService.deleteSubmissionInElastic(this.data.submission.id, type)
            .pipe()
            .subscribe(
              (data: any) => {
                this.spinner.hide();
                swal.close()
                // if(!this.isConfirmed && !this.deleteDocumentInArchive && !this.deleteDocumentInRepository){
                //   swal.fire('', `${paragraph}`, 'info').then((result) => {
                //     if (result.isConfirmed) { 
                //       if (this.loggedInUser.roleId === 10) {
                //         window.location.replace(`${environment.ioApplicationUrl}professor/submissions`)
                //       }

                //       if (this.loggedInUser.roleId === 9) {
                //         window.location.replace(`${environment.ioApplicationUrl}student/submissions`)
                //       }
                //     }
                //     this.isConfirmed = false;
                //   })
                // }
                if(type == 1) { this.deleteDocumentInArchive = true; 
                  localStorage.setItem(
                    `deletedDocument-${this.data.submission.id}`,
                    'true'
                  );
                }
                // this.submissionsService.deleteSubmissionInElastic(this.data.submission.id, type)
                //   .subscribe((res) => {
                //   });
                if(type == 2) { 
                  this.deleteDocumentInRepository = true;
                  if((this.loggedInUser.roleId === 9) || (this.loggedInUser.roleId === 10) || (this.loggedInUser.roleId === 11) || (this.checkEducatorPrecheck) || (this.checkStudentPrecheck))
                   { window.location.replace(`${environment.currentUrl}deleted-submission`); }
                }  
              },
              (error) => {
                console.log('error', error);
                this.spinner.hide();
              }
            );
        }
      });
      if(type == 2) { this.cancel(); }
  }

  /**
   * Get duration preview in human-readable format
   */
  get durationPreview(): string {
    if (!this.timeBasedEndDate) {
      return '';
    }

    const now = new Date();
    const endDate = new Date(this.timeBasedEndDate);
    const diffMs = endDate.getTime() - now.getTime();

    if (diffMs <= 0) {
      return this.translate.instant('share_report.invalid_date');
    }

    return this.formatDuration(diffMs);
  }

  /**
   * Format duration in milliseconds to human-readable format
   */
  private formatDuration(durationMs: number): string {
    const totalSeconds = Math.floor(durationMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;

    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }

    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }

    if (minutes > 0 && days === 0) { // Don't show minutes if we have days
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }

    // For very short durations (less than 1 minute), show seconds
    if (totalMinutes === 0 && seconds > 0) {
      parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      return this.translate.instant('share_report.less_than_a_second');
    }

    // Join with "and" for the last part
    if (parts.length > 1) {
      const lastPart = parts.pop();
      return parts.join(', ') + ' and ' + lastPart;
    }

    return parts[0];
  }

  /**
   * Toggle row expansion (accordion-style - only one row expanded at a time)
   */
  toggleRowExpansion(shareId: string): void {
    if (this.expandedRows.has(shareId)) {
      // If clicking on the already expanded row, collapse it
      this.expandedRows.delete(shareId);
    } else {
      // Collapse all other rows and expand this one
      this.expandedRows.clear();
      this.expandedRows.add(shareId);
    }
  }

  /**
   * Check if row is expanded
   */
  isRowExpanded(shareId: string): boolean {
    return this.expandedRows.has(shareId);
  }

  /**
   * Toggle sharer expansion (for grouping shares by user)
   */
  toggleSharerExpansion(sharerId: string): void {
    if (this.expandedSharers.has(sharerId)) {
      this.expandedSharers.delete(sharerId);
    } else {
      this.expandedSharers.clear(); // Only allow one expanded at a time
      this.expandedSharers.add(sharerId);
    }
  }

  /**
   * Check if a sharer row is expanded
   */
  isSharerExpanded(sharerId: string): boolean {
    return this.expandedSharers.has(sharerId);
  }

  /**
   * Toggle the others with access table visibility
   */
  toggleOthersTable(): void {
    this.isOthersTableExpanded = !this.isOthersTableExpanded;
  }

  /**
   * Clear the generated link and related messages
   */
  private clearGeneratedLink(): void {
    this.generatedShareLink = '';
    this.linkCopyMessage = '';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null): string {
    if (!dateString) {
      return this.translate.instant('share_report.never');
    }

    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }

  /**
   * Get share type display text
   * Note: For received shares, this will always be 'Email Share' as the
   * /user/shares/received endpoint now only returns email shares
   */
  getShareTypeDisplayText(shareType: string): string {
    switch (shareType) {
      case 'email_share':
        return this.translate.instant('share_report.email_share');
      case 'link_share':
        return this.translate.instant('share_report.link_share');
      default:
        return shareType;
    }
  }


  shouldShowArchiveBg(): boolean {
    return this.data.submission.archive === true && this.data.submission.archive === true;
  }

  getCorrectedText() {
    if(this.data.submission.archive) {
      return {
        text: this.translate.instant('reports_io.action_irreversible_document_removed'),
        backgroundColor: '#FEF1F1',
      };
    }
    else if(!this.isDocumentInArchive) {
      return {
        text: this.translate.instant('reports_io.unavailable_action'),
        backgroundColor: '#ffffff',
      };
    }
  }
 

  /**
   * Get access description for other shares
   */
  getAccessDescription(share: OtherShareInfo): string {
    const sharedDate = this.formatDate(share.shareCreatedAt);

    if (share.accessType === 'time_based') {
      return `${sharedDate} (${this.translate.instant('share_report.time_based')})`;
    } else if (share.accessType === 'unlimited') {
      return `${this.translate.instant('share_report.since')} ${sharedDate} (${this.translate.instant('share_report.unlimited')})`;
    } else if (share.accessType === 'one_time') {
      return `${sharedDate} (${this.translate.instant('share_report.one_time')})`;
    }

    return `${sharedDate} (${share.accessType})`;
  }

  /**
   * Get the latest share date from a group of shares
   */
  getLatestShareDate(shares: OtherShareInfo[]): string {
    if (!shares || shares.length === 0) {
      return 'N/A';
    }

    const latestShare = shares.reduce((latest, current) => {
      const latestDate = new Date(latest.shareCreatedAt);
      const currentDate = new Date(current.shareCreatedAt);
      return currentDate > latestDate ? current : latest;
    });

    return this.formatDate(latestShare.shareCreatedAt);
  }

  /**
   * Get current share access description for recipients
   */
  getCurrentShareAccessDescription(): string {
    if (!this.currentShareDetails) {
      return this.translate.instant('share_report.no_share_details');
    }

    const share = this.currentShareDetails.share;
    const sharedDate = this.formatDate(share.shareCreatedAt);

    let description = `${this.translate.instant('share_report.shared_by')} ${share.sharedBy.name} ${this.translate.instant('share_report.on')} ${sharedDate}`;

    if (share.accessType === 'time_based' && share.expiresAt) {
      const expiryDate = this.formatDate(share.expiresAt);
      description += ` - ${this.translate.instant('share_report.expires')}: ${expiryDate}`;

      if (share.status === 'expired') {
        description += ` (${this.translate.instant('share_report.expired')})`;
      }
    } else if (share.accessType === 'unlimited') {
      description += ` (${this.translate.instant('share_report.unlimited_access')})`;
    } else if (share.accessType === 'one_time') {
      description += ` (${this.translate.instant('share_report.one_time_access')})`;

      if (share.status === 'used') {
        description += ` - ${this.translate.instant('share_report.used')}`;
      }
    }

    return description;
  }

}


export class DialogContentExampleDialog { }
